import { IconStyles } from '@dynamic/ui';
import { InitOptions } from 'i18next';

import {
  CornerRadius,
  FormFactors,
  MenuBackgroundStyle,
  MenuFocusStyle,
} from '#/service/constants';
import { authActions } from '#/store/auth/auth.store';

import { MenuType } from '../constants/menu';
import { MenuStyle } from './common.types';
import { MainConfig, MenuConfig, ThemeConfig } from './config.types';
import {
  defaultAppConfig,
  defaultConfig,
  defaultFeaturesConfig,
  defaultI18nextConfig,
  defaultThemeConfig,
} from './defaultConfig';
import {
  CoreFeatureConfig,
  FeaturesConfig,
  RawFeaturesConfig,
} from './features.types';
import { RawConfig, RawMenuConfig, RawThemeConfig } from './raw.types';

/** This function gets inline config in the document and transforms it for our domain. It also applies default values where necessary */
export function initializeConfig(
  config: RawConfig,
  i18nConfig?: InitOptions,
  staticFilePrefix?: string,
  version?: string,
  routerVersion?: string
): MainConfig {
  if (!config) {
    return defaultConfig;
  }

  const authConfig = config.featureConfig.authentication ?? {};
  authActions.setConfig(authConfig);

  return {
    app: {
      staticFilePrefix,
      version,
      routerVersion,
      ...defaultAppConfig,
      // app comes from legacy JSON config.
      ...config.app,
      // application comes from core directly. We currently override "app" with core's application values due to the migration
      // away from legacy JSON config. Once we fully deprecate the legacy config, we can remove config.app completely.
      ...config.application,
    },
    i18nextConfig: i18nConfig ?? defaultI18nextConfig,
    menusConfig: config.menusConfig ?? {
      groups: [],
      style: {},
    },
    theme: prepareTheme(config.theme, config.menusConfig?.style),
    menu: prepareMenu(config.menus),
    more: { settings: config.more ?? [] },
    footer: config.footer ?? {
      enabled: false,
      copyright: '',
      contact: '',
      socials: [],
      columns: [],
    },
    features: prepareFeatures(config.features, config.featureConfig),
    featureConfig: config.featureConfig,
    featureFlags: config.featureFlags ?? {},
    pages: config.pages ?? {},
    terms: config.terms ?? {
      body: '',
      date: '',
    },
    restrictions: {
      geoblocked: false,
      ...config.restrictions, // from S3
      ...config.runtime?.restrictions, // from Core
    },
    runtime: config.runtime ?? {},
    integrations: {
      ...config.integrations,
      // the analytics integration configs are not yet coming from Core. they were added to S3 for now. to prevent the
      // web backend to overwrite S3 integrations with Core integrations, we renamed the S3 one to analyticsIntegrations,
      // and merging them here. when Core is ready, this can be removed. also remove from types, and from S3 config.
      ...config.analyticsIntegrations,
    },
    coreAnalyticsEvents: config.coreAnalyticsEvents ?? [],
    legacyAnalyticsEvents: config.legacyAnalyticsEvents ?? {},
    pluginIntegrations: config.pluginIntegrations ?? {},
    integrationsVersionOverrides: config.integrationsVersionOverrides ?? {},
    webConfig: config.webConfig ?? {},
  };
}

export function prepareTheme(
  theme: RawThemeConfig,
  menuStyle?: MenuStyle
): ThemeConfig {
  const newTheme: ThemeConfig = {
    color: { ...defaultConfig.theme.color, ...theme.color },
    images: theme.images,
    // We are hardcoding fonts configuration in the Web client
    fonts: defaultThemeConfig.fonts,
    gradientColor: theme.gradientColor,
    button: theme.button,
    menu: {
      focus: menuStyle?.focus ?? MenuFocusStyle.Box,
      background: menuStyle?.background ?? MenuBackgroundStyle.Gradient,
    },
    icon: theme.icon ?? {
      navigation: { style: IconStyles.Fill },
      parentalGuidance: { style: IconStyles.Fill },
    },
    cornerRadius: theme.cornerRadius ?? CornerRadius.SMALL,
    staticPathIcon: theme.staticPathIcon ?? '/static/plugins/icons/',
    logo: theme.logo,
    pageBackgroundImage: theme.pageBackgroundImage,
  };

  return newTheme;
}

function filterEnabled<T extends { enabled: boolean }>(arr: T[]): T[] {
  return arr.filter((f) => f.enabled);
}

/**
 * It filters out not enabled features in a dynamic way and applies Features defined in the Core API
 **/
function prepareFeatures(
  features: RawFeaturesConfig,
  coreFeatures: CoreFeatureConfig
): FeaturesConfig {
  // filter for enabled entries in each array feature
  const enabledArrayFeatures = Object.fromEntries(
    Object.entries(features ?? {})
      .filter(([_key, arr]) => Array.isArray(arr))
      .map(([key, arr]) => [key, filterEnabled(arr)])
  );

  const result: Partial<FeaturesConfig> = {
    search: coreFeatures.search,
    playback: coreFeatures.playback,
    purchase: coreFeatures.purchase,
    ...enabledArrayFeatures,
  };

  // Let's use default features config as a fallback
  return { ...defaultFeaturesConfig, ...result };
}

export function prepareMenu(menus?: RawMenuConfig): MenuConfig {
  return {
    type: menus?.type ?? MenuType.TOP,
    mainMenuItems: menus?.[FormFactors.DESKTOP]?.menus[0]?.items ?? [],
    linkMenuItems: menus?.[FormFactors.DESKTOP]?.menus[1]?.items ?? [],
  };
}
